import React from 'react';

const RELOAD_FLAG = 'rxr_chunk_reload_attempted';

/**
 * Detect the various shapes a code-split load failure can take. Webpack throws
 * `ChunkLoadError` for JS chunks and "Loading CSS chunk N failed" for CSS
 * chunks; React.lazy surfaces both through Suspense to the nearest boundary.
 */
export function isChunkLoadError(error) {
  if (!error) return false;
  const name = error.name || '';
  const message = error.message || '';
  return (
    name === 'ChunkLoadError' ||
    /Loading (CSS )?chunk [\w-]+ failed/i.test(message) ||
    /Loading chunk [\w-]+ failed/i.test(message)
  );
}

/**
 * Catches render-time errors from lazy-loaded routes so a single failing chunk
 * degrades to a recoverable message instead of unmounting the entire React tree
 * (which renders a blank page).
 *
 * For chunk-load errors the most common cause is a stale deploy: the user's
 * cached index.html references chunk filenames that no longer exist after a new
 * build. A single reload pulls the fresh manifest and fixes it. We attempt that
 * reload exactly once (guarded by sessionStorage) so a genuinely broken chunk
 * cannot trap the user in a reload loop; the second time, we show the fallback.
 */
class RouteErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, isChunkError: false };
    this.handleReload = this.handleReload.bind(this);
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, isChunkError: isChunkLoadError(error) };
  }

  componentDidCatch(error, info) {
    if (isChunkLoadError(error)) {
      let alreadyTried = false;
      try {
        alreadyTried =
          window.sessionStorage.getItem(RELOAD_FLAG) === '1';
      } catch {
        // sessionStorage can be unavailable (private mode); fall through.
      }
      if (!alreadyTried) {
        try {
          window.sessionStorage.setItem(RELOAD_FLAG, '1');
        } catch {
          // ignore
        }
        window.location.reload();
        return;
      }
    }
    // eslint-disable-next-line no-console
    console.error('RouteErrorBoundary caught an error:', error, info);
  }

  handleReload() {
    try {
      window.sessionStorage.removeItem(RELOAD_FLAG);
    } catch {
      // ignore
    }
    window.location.reload();
  }

  render() {
    if (!this.state.hasError) {
      return this.props.children;
    }

    return (
      <div
        role="alert"
        style={{
          maxWidth: 480,
          margin: '15vh auto 0',
          padding: '0 24px',
          textAlign: 'center',
          fontFamily: 'inherit',
        }}
      >
        <h1 style={{ fontSize: 20, fontWeight: 600, marginBottom: 8 }}>
          Something went wrong loading this page
        </h1>
        <p style={{ fontSize: 14, color: '#555', marginBottom: 20 }}>
          {this.state.isChunkError
            ? 'This usually means a new version was just released. Reloading should fix it.'
            : 'An unexpected error occurred. Reloading often resolves it.'}
        </p>
        <button
          type="button"
          onClick={this.handleReload}
          style={{
            background: '#C75B39',
            color: '#fff',
            border: 'none',
            borderRadius: 6,
            padding: '10px 20px',
            fontSize: 14,
            fontWeight: 600,
            cursor: 'pointer',
          }}
        >
          Reload page
        </button>
      </div>
    );
  }
}

export default RouteErrorBoundary;
